import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";

import NotificationOptIn from "./components/NotificationOptIn";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import Pricing from "@/pages/Pricing";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Booking from "@/pages/Booking";
import BookingSuccess from "@/pages/BookingSuccess";
import BookingTrack from "@/pages/BookingTrack";
import JoinTechnician from "@/pages/JoinTechnician";
import Privacy from "@/pages/Privacy";
import ServiceLanding from "@/pages/ServiceLanding";
import PageMetadata from "@/seo/PageMetadata";
import { SERVICE_PAGES } from "@/seo/pages";

// Admin
import { AuthProvider } from "@/admin/AuthContext";
import ProtectedRoute from "@/admin/ProtectedRoute";
import AdminLogin from "@/admin/pages/AdminLogin";
import AdminDashboard from "@/admin/pages/AdminDashboard";
import AdminPayouts from "@/admin/pages/AdminPayouts";
import AdminAMC from "@/admin/pages/AdminAMC";
import AdminBookings from "@/admin/pages/AdminBookings";
import AdminBookingDetail from "@/admin/pages/AdminBookingDetail";
import AdminApplications from "@/admin/pages/AdminApplications";
import AdminApplicationDetail from "@/admin/pages/AdminApplicationDetail";
import AdminTechnicians from "@/admin/pages/AdminTechnicians";
import AdminTechnicianDetail from "@/admin/pages/AdminTechnicianDetail";
import AdminCustomers from "@/admin/pages/AdminCustomers";
import AdminCustomerDetail from "@/admin/pages/AdminCustomerDetail";
import AdminCatalog from "@/admin/pages/AdminCatalog";
import AdminSettings from "@/admin/pages/AdminSettings";

// Customer
import { CustomerProvider } from "@/contexts/CustomerContext";
import { CatalogProvider } from "@/contexts/CatalogContext";
import CustomerDashboard from "@/pages/CustomerDashboard";
import BookingDetailPage from "@/pages/BookingDetailPage";
import CustomerSettings from "@/pages/CustomerSettings";
import MobileLoginModal from "@/components/MobileLoginModal";
import useBookingAuthInterceptor from "@/hooks/useBookingAuthInterceptor";

import { Toaster } from "sonner";

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      document.getElementById(hash.slice(1))?.scrollIntoView();
      return;
    }
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
}

const Public = ({ children }) => <Layout>{children}</Layout>;

const AdminWrapper = ({ children, roles }) => (
  <>
    <ProtectedRoute roles={roles}>{children}</ProtectedRoute>
    <Toaster position="top-center" richColors />
  </>
);

// Component to handle booking auth interceptor (must be inside CustomerProvider)
function AppContent() {
  useBookingAuthInterceptor();

  return (
    <>
      <PageMetadata />
      <NotificationOptIn />
      <MobileLoginModal />
      <Routes>
        {/* Public site */}
        <Route path="/" element={<Public><Home /></Public>} />
        {SERVICE_PAGES.map(page => (
          <Route key={page.path} path={page.path} element={<Public><ServiceLanding page={page} /></Public>} />
        ))}
        <Route path="/services" element={<Public><Services /></Public>} />
        <Route path="/pricing" element={<Public><Pricing /></Public>} />
        <Route path="/about" element={<Public><About /></Public>} />
        <Route path="/contact" element={<Public><Contact /></Public>} />
        <Route path="/booking" element={<Public><Booking /></Public>} />
        <Route path="/booking/success/:id" element={<Public><BookingSuccess /></Public>} />
        <Route path="/booking/track/:id" element={<Public><BookingTrack /></Public>} />
        <Route path="/join-as-technician" element={<Public><JoinTechnician /></Public>} />
        <Route path="/privacy" element={<Public><Privacy /></Public>} />

        {/* Customer Dashboard */}
        <Route path="/dashboard" element={<CustomerDashboard />} />
        <Route path="/dashboard/bookings/:booking_id" element={<BookingDetailPage />} />
        <Route path="/dashboard/settings" element={<CustomerSettings />} />

        {/* Admin */}
        <Route path="/admin/login" element={<><AdminLogin /><Toaster position="top-center" richColors /></>} />
        <Route path="/admin" element={<AdminWrapper><AdminDashboard /></AdminWrapper>} />
        <Route path="/admin/payouts" element={<AdminWrapper><AdminPayouts /></AdminWrapper>} />
        <Route path="/admin/amc" element={<AdminWrapper><AdminAMC /></AdminWrapper>} />
        <Route path="/admin/bookings" element={<AdminWrapper><AdminBookings /></AdminWrapper>} />
        <Route path="/admin/bookings/:id" element={<AdminWrapper><AdminBookingDetail /></AdminWrapper>} />
        <Route path="/admin/applications" element={<AdminWrapper><AdminApplications /></AdminWrapper>} />
        <Route path="/admin/applications/:ref" element={<AdminWrapper><AdminApplicationDetail /></AdminWrapper>} />
        <Route path="/admin/catalog" element={<AdminWrapper><AdminCatalog /></AdminWrapper>} />
        <Route path="/admin/technicians" element={<AdminWrapper><AdminTechnicians /></AdminWrapper>} />
        <Route path="/admin/technicians/:id" element={<AdminWrapper><AdminTechnicianDetail /></AdminWrapper>} />
        <Route path="/admin/customers" element={<AdminWrapper><AdminCustomers /></AdminWrapper>} />
        <Route path="/admin/customers/:phone" element={<AdminWrapper><AdminCustomerDetail /></AdminWrapper>} />
        <Route path="/admin/settings" element={<AdminWrapper><AdminSettings /></AdminWrapper>} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <CatalogProvider>
            <CustomerProvider>
              <AppContent />
              <Toaster position="top-center" richColors />
            </CustomerProvider>
          </CatalogProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
